import { useQuery } from '@tanstack/react-query';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { getErrorInfoFromStatusCode } from 'src/utils/errorUtils';
import {
  EmptyEntityWithTicketsQuery,
  QueryWithViewMode,
} from 'src/utils/eventQueryUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  CatalogResults,
  EntityWithTicketsQuery,
  EventWithData,
} from 'src/WebApiController';

import { useAppContext } from '../AppContext';
import { useCatalogDataContext } from '../CatalogDataContext';
import { useFilterQueryContext } from '../FilterQueryContext';
import {
  SearchDataContext,
  SearchDataContextProviderProps,
} from './SearchDataContext.types';
import {
  buildCatalogAndEventsFromListingsSearchData,
  buildCatalogAndEventsFromSalesSearchData,
} from './SearchDataContext.utils';

export const useSearchDataContext = () => useContext(SearchDataContext);

/**
 * Context provider to retrieve search data from the server, and provide a similar interface to the catalog data context when consumed by table components.
 *
 * The search data context requests search data from the server which contains catalog data and entity data. This is later transformed into catalog data and entity data.
 * The context optionally provides a fallback to the catalog data context when the catalog data from search data is not available.
 */
export function SearchDataContextProvider<
  TQuery extends EntityWithTicketsQuery & QueryWithViewMode,
>({
  queryKey,
  entityType,
  getSearchData,
  children,
  disabled,
}: SearchDataContextProviderProps<TQuery>) {
  const { showErrorDialog } = useClientLogContext();
  const { filterQuery, isQueryInitialized } = useFilterQueryContext<TQuery>();
  const { activeAccountWebClientConfig } = useAppContext();

  const { data: catalogDataFallback } = useCatalogDataContext();

  const [errorInfo, setErrorInfo] = useState<{
    errorHeader: React.ReactNode;
    errorMessage: React.ReactNode;
  }>();

  const {
    searchText,
    viewMode, // this is not used for server and keeping it in this cause refresh to happen 2x
    ...serverSideFilterQuery // Don't use it because it's a new object on every render
  } = filterQuery;

  const searchDataFilterQuery = useMemo(() => {
    const query = Object.fromEntries(
      Object.entries(serverSideFilterQuery).filter(([_, v]) => v != null)
    );
    return {
      ...query,
      timezoneOffsetMins:
        query.timezoneOffsetMins ??
        EmptyEntityWithTicketsQuery.timezoneOffsetMins,
    } as TQuery;
  }, [serverSideFilterQuery]);

  const shouldQuerySearchData = useMemo(
    () =>
      activeAccountWebClientConfig.activeAccountId != null &&
      searchDataFilterQuery != null &&
      isQueryInitialized &&
      !disabled,
    [
      activeAccountWebClientConfig.activeAccountId,
      searchDataFilterQuery,
      isQueryInitialized,
      disabled,
    ]
  );

  const searchDataQueryKey = useMemo(
    () => [queryKey + 'Expanded', searchDataFilterQuery, shouldQuerySearchData],
    [queryKey, searchDataFilterQuery, shouldQuerySearchData]
  );

  const searchDataQuery = useQuery({
    queryKey: [searchDataQueryKey],
    queryFn: async () => {
      if (!shouldQuerySearchData) {
        return null;
      }

      return tryInvokeApi(
        async () => {
          const data = await getSearchData?.(
            [],
            searchDataFilterQuery as TQuery
          );

          return data;
        },
        (error: ErrorTypes) => {
          showErrorDialog('getSearchData', error, {
            trackErrorData: {
              eventIds: [],
              filterQuery,
            },
          });
        }
      );
    },
    enabled: shouldQuerySearchData,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchInterval: false,
  });

  // Handle query errors
  useEffect(() => {
    const failureReason =
      shouldQuerySearchData && searchDataQuery.failureReason;

    if (failureReason) {
      const { headerDisplay, messageDisplay } = getErrorInfoFromStatusCode(
        null,
        failureReason?.toString()
      );
      setErrorInfo({
        errorHeader: headerDisplay,
        errorMessage: messageDisplay,
      });
    } else if (errorInfo) {
      setErrorInfo(undefined);
    }
  }, [errorInfo, searchDataQuery.failureReason, shouldQuerySearchData]);

  const { catalogResults, eventsTransformed } = useMemo(() => {
    if (searchDataQuery.data == null) {
      return {
        catalogResults: {} as CatalogResults,
        eventsTransformed: [] as EventWithData[],
      };
    }

    if (entityType === ActionOutboxEntityType.Sale) {
      const result = buildCatalogAndEventsFromSalesSearchData(
        searchDataQuery.data,
        catalogDataFallback
      );

      return result;
    }

    if (entityType === ActionOutboxEntityType.Listing) {
      const result = buildCatalogAndEventsFromListingsSearchData(
        searchDataQuery.data,
        catalogDataFallback
      );

      return result;
    }

    return {
      catalogResults: {} as CatalogResults,
      eventsTransformed: [] as EventWithData[],
    };
  }, [entityType, searchDataQuery.data, catalogDataFallback]);

  return (
    <SearchDataContext.Provider
      value={{
        isEnabled: !disabled,
        isLoading: searchDataQuery.isFetching,
        data: catalogResults ?? undefined,
        errorInfo,
        eventsTransformed,
        refetch: searchDataQuery.refetch,
      }}
    >
      {children}
    </SearchDataContext.Provider>
  );
}
